import React, { useState } from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { UserIcon, BellIcon, CalendarIcon, ShieldIcon, LogOutIcon, ChevronRightIcon, DownloadIcon, TrashIcon, LanguagesIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
interface SettingsProps {
  onNavigateToProfile: () => void;
  onNavigateToEventPreference: () => void;
}
export function Settings({
  onNavigateToProfile,
  onNavigateToEventPreference
}: SettingsProps) {
  const {
    currentEvent,
    isOnline,
    lastSyncTime
  } = useEvent();
  const {
    logout
  } = useAuth();
  const {
    language,
    setLanguage,
    t,
    isRTL
  } = useLanguage();
  const [notifications, setNotifications] = useState({
    promotions: true,
    reminders: true,
    updates: true,
    networking: false
  });
  const formatSyncTime = () => {
    if (!lastSyncTime) return 'Never';
    return lastSyncTime.toLocaleString();
  };
  return <div className="pb-24" data-id="element-4116">
      {/* Profile Section */}
      <div className="p-4" data-id="element-4117">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 font-gotham" data-id="element-4118">
          {t('settings.account')}
        </h3>

        <Card padding="none" data-id="element-4119">
          <button onClick={onNavigateToProfile} className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-4120">
            <div className="flex items-center gap-3" data-id="element-4121">
              <UserIcon className="w-5 h-5 text-gray-600" data-id="element-4122" />
              <p className="font-medium text-gray-900 font-gotham" data-id="element-4123">
                {t('settings.profile')}
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" style={{
            transform: isRTL ? 'rotate(180deg)' : 'none'
          }} data-id="element-4124" />
          </button>

          <button onClick={onNavigateToEventPreference} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-id="element-4125">
            <div className="flex items-center gap-3" data-id="element-4126">
              <CalendarIcon className="w-5 h-5 text-gray-600" data-id="element-4127" />
              <div className={`text-${isRTL ? 'right' : 'left'}`} data-id="element-4128">
                <p className="font-medium text-gray-900 font-gotham" data-id="element-4129">
                  {t('settings.eventPreference')}
                </p>
                <p className="text-sm text-gray-500 font-gotham" data-id="element-4130">
                  {t('settings.currentlyLabel')}: {currentEvent.name}
                </p>
              </div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" style={{
            transform: isRTL ? 'rotate(180deg)' : 'none'
          }} data-id="element-4131" />
          </button>
        </Card>
      </div>

      {/* Language Selection */}
      <div className="p-4" data-id="element-4132">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 font-gotham" data-id="element-4133">
          {t('settings.language')}
        </h3>

        <Card padding="none" data-id="element-4134">
          <button onClick={() => setLanguage('en')} className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-4135">
            <div className="flex items-center gap-3 flex-1" data-id="element-4136">
              <LanguagesIcon className="w-5 h-5 text-gray-600" data-id="element-4137" />
              <p className="font-medium text-gray-900 font-gotham" data-id="element-4138">
                {t('settings.english')}
              </p>
            </div>
            {language === 'en' && <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{
            backgroundColor: currentEvent.colors.primary
          }} data-id="element-4139">
                <div className="w-2 h-2 bg-white rounded-full" data-id="element-4140" />
              </div>}
          </button>

          <button onClick={() => setLanguage('ar')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-id="element-4141">
            <div className="flex items-center gap-3 flex-1" data-id="element-4142">
              <LanguagesIcon className="w-5 h-5 text-gray-600" data-id="element-4143" />
              <p className="font-medium text-gray-900 font-gotham" data-id="element-4144">
                {t('settings.arabic')}
              </p>
            </div>
            {language === 'ar' && <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{
            backgroundColor: currentEvent.colors.primary
          }} data-id="element-4145">
                <div className="w-2 h-2 bg-white rounded-full" data-id="element-4146" />
              </div>}
          </button>
        </Card>
      </div>

      {/* Notifications */}
      <div className="p-4" data-id="element-4147">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 font-gotham" data-id="element-4148">
          {t('settings.notifications')}
        </h3>

        <Card padding="none" data-id="element-4149">
          {Object.entries(notifications).map(([key, value], index) => <div key={key} className={`flex items-center justify-between p-4 ${index !== Object.keys(notifications).length - 1 ? 'border-b border-gray-100' : ''}`} data-id="element-4150">
              <div className="flex-1" data-id="element-4151">
                <p className="font-medium text-gray-900 font-gotham capitalize" data-id="element-4152">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                <p className="text-sm text-gray-500 font-gotham" data-id="element-4153">
                  {key === 'promotions' && 'Receive promotional offers and deals'}
                  {key === 'reminders' && 'Session and event reminders'}
                  {key === 'updates' && 'Floor plan and schedule updates'}
                  {key === 'networking' && 'New networking matches'}
                </p>
              </div>
              <button onClick={() => setNotifications({
            ...notifications,
            [key]: !value
          })} className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${isRTL ? 'mr-2' : 'ml-2'} ${value ? '' : 'bg-gray-300'}`} style={value ? {
            backgroundColor: currentEvent.colors.primary
          } : {}} data-id="element-4154">
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${value ? isRTL ? '-translate-x-6' : 'translate-x-6' : isRTL ? '-translate-x-0.5' : 'translate-x-0.5'}`} data-id="element-4155" />
              </button>
            </div>)}
        </Card>
      </div>

      {/* Data & Storage */}
      <div className="p-4" data-id="element-4156">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 font-gotham" data-id="element-4157">
          {t('settings.dataStorage')}
        </h3>

        <Card padding="none" data-id="element-4158">
          <button className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-4159">
            <div className="flex items-center gap-3 flex-1" data-id="element-4160">
              <DownloadIcon className="w-5 h-5 text-gray-600" data-id="element-4161" />
              <div className={`text-${isRTL ? 'right' : 'left'}`} data-id="element-4162">
                <p className="font-medium text-gray-900 font-gotham" data-id="element-4163">
                  {t('settings.offlineData')}
                </p>
                <p className="text-sm text-gray-500 font-gotham" data-id="element-4164">
                  {t('settings.lastSynced')}: {formatSyncTime()}
                </p>
              </div>
            </div>
            <Badge variant={isOnline ? 'success' : 'warning'} size="sm" data-id="element-4165">
              {isOnline ? t('settings.online') : t('settings.offline')}
            </Badge>
          </button>

          <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-id="element-4166">
            <div className="flex items-center gap-3 flex-1" data-id="element-4167">
              <TrashIcon className="w-5 h-5 text-gray-600" data-id="element-4168" />
              <div className={`text-${isRTL ? 'right' : 'left'}`} data-id="element-4169">
                <p className="font-medium text-gray-900 font-gotham" data-id="element-4170">
                  {t('settings.clearCache')}
                </p>
                <p className="text-sm text-gray-500 font-gotham" data-id="element-4171">
                  {t('settings.freeUp')} 24 MB
                </p>
              </div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" style={{
            transform: isRTL ? 'rotate(180deg)' : 'none'
          }} data-id="element-4172" />
          </button>
        </Card>
      </div>

      {/* Account Actions */}
      <div className="p-4" data-id="element-4173">
        <Card padding="none" data-id="element-4174">
          <button className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-4175">
            <div className="flex items-center gap-3" data-id="element-4176">
              <ShieldIcon className="w-5 h-5 text-gray-600" data-id="element-4177" />
              <p className="font-medium text-gray-900 font-gotham" data-id="element-4178">
                {t('settings.privacySecurity')}
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" style={{
            transform: isRTL ? 'rotate(180deg)' : 'none'
          }} data-id="element-4179" />
          </button>

          <button onClick={logout} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-id="element-4180">
            <div className="flex items-center gap-3" data-id="element-4181">
              <LogOutIcon className="w-5 h-5 text-red-600" data-id="element-4182" />
              <p className="font-medium text-red-600 font-gotham" data-id="element-4183">
                {t('settings.signOut')}
              </p>
            </div>
          </button>
        </Card>
      </div>

      {/* App Info */}
      <div className="p-4 text-center" data-id="element-4184">
        <p className="text-sm text-gray-500 font-gotham mb-1" data-id="element-4185">IEG App v1.0.0</p>
        <p className="text-xs text-gray-400 font-gotham" data-id="element-4186">
          {t('settings.poweredBy')} {currentEvent.name}
        </p>
      </div>
    </div>;
}